import React, { useState, useEffect, useCallback } from 'react';
// import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from "../../components/ui/Footer";
import mockData from "/src/pages/product-profile-page/mockData.json"
import { fetchAuthSession } from "aws-amplify/auth";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { Heart, Bookmark, PlusCircle } from "lucide-react";
import ProductTable from "./components/ProductTable";
// import awsExports from '../../aws-exports';

// const isLoggedIn = Boolean(localStorage.getItem("user")); // or from context/auth provider


const API_BASE = import.meta.env.DEV ? '/api' : 'https://api.cosmelytix.com';

const ProductsHomePage = () => {
  // State for products data, loading, and error
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [savedProducts, setSavedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // State for search, pagination, and sorting
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showLoginNotice, setShowLoginNotice] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // 20 items per page
  
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc"
  });

  const [idToken, setIdToken] = useState(null);

  const [interactions, setInteractions] = useState({
    save: new Set(),
    like: new Set(),
    add: new Set(),
  });

  // Get auth token on mount
  useEffect(() => {
    const getToken = async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        let token = null;
        if (typeof session.tokens?.idToken === "string") {
          token = session.tokens.idToken;
        } else if (session.tokens?.idToken?.jwtToken) {
          token = session.tokens.idToken.jwtToken;
        } else if (session.tokens?.idToken?.toString) {
          token = session.tokens.idToken.toString();
        }
        if (token) {
          setIdToken(token);
          setIsAuthenticated(true);
        }
      } catch {
        // not logged in
      }
    };
    getToken();
  }, []);

  // Fetch interaction statuses for all loaded products
  useEffect(() => {
    if (!idToken || products.length === 0) return;

    const fetchStatuses = () => {
      const types = ['like', 'save', 'add'];
      products.forEach(p => {
        types.forEach(type => {
          fetch(`${API_BASE}/interaction?productId=${p.id}&type=${type}`, {
            headers: { Authorization: `Bearer ${idToken}` },
          })
            .then(async res => {
              if (res.status === 404) return;
              const wrapper = await res.json();
              const data = typeof wrapper.body === 'string' ? JSON.parse(wrapper.body) : (wrapper.body ?? wrapper);
              if (data?.status === 1) {
                setInteractions(prev => ({
                  ...prev,
                  [type]: new Set([...prev[type], p.id]),
                }));
              }
            })
            .catch(() => {});
        });
      });
    };

    fetchStatuses();
  }, [products, idToken]);

  const handleInteraction = async (productId, type) => {
    // Optimistic update
    setInteractions(prev => {
      const updated = new Set(prev[type]);
      updated.has(productId) ? updated.delete(productId) : updated.add(productId);
      return { ...prev, [type]: updated };
    });

    try {
      const res = await fetch(`${API_BASE}/interaction`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productId: String(productId), interactionType: type }),
      });
      if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
    } catch (err) {
      console.error(`Failed to record ${type}:`, err);
      // Revert on failure
      setInteractions(prev => {
        const updated = new Set(prev[type]);
        updated.has(productId) ? updated.delete(productId) : updated.add(productId);
        return { ...prev, [type]: updated };
      });
    }
  };
  
  

  // Fetch products data from the API
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let mappedProducts = [];
  
        if (currentPage === 1) {
          // First page: load from mockData
          const firstPageProducts = mockData.slice(0, itemsPerPage);
          mappedProducts = firstPageProducts.map(item => ({
            id: item.prod_id,
            name: item.title,
            brand: item.brand || 'N/A',
            image: item.image_url,
            description: item.description
              ? item.description.length > 40
                ? item.description.substring(0, 40) + '...'
                : item.description
              : 'No description provided.',
            // novelty: item.novelty || 'New',
            // popularity: item.popularity || 0,
            // sustainability: item.sustainability || 'N/A',
          }));
        } else {

          // checkAuthAndFetchProduct()

          // Subsequent pages: fetch from API
          // console.log('currentPage', currentPage, `https://api.cosmelytix.com/products?page=${currentPage}&pageSize=${itemsPerPage}`)
          const response = await fetch(`${API_BASE}/products?page=${currentPage}&pageSize=${itemsPerPage}`);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          // console.log('resp', response)
          const responseData = await response.json();
          // console.log('responseData', responseData)
          // Case: API directly returns array
if (Array.isArray(responseData)) {
  mappedProducts = responseData.map(item => ({
    ...item,
    id: item.prod_id || item.id,
    image: item.image_url,
  }));
}
// Case: API wraps array inside body
else if (responseData && responseData.body && Array.isArray(responseData.body)) {
            mappedProducts = responseData.body.map(item => ({
              id: item.prod_id,
              name: item.name,
              brand: item.brand || 'N/A',
              image: item.image_url,
              description: item.description
                ? item.description.length > 40
                  ? item.description.substring(0, 40) + '...'
                  : item.description
                : 'No description provided.',
              // novelty: 'New',
              // popularity: item.popularity || 0,
              // sustainability: item.sustainability || 'N/A',
            }));
          }
        }
  
        setProducts(mappedProducts);
        setFilteredProducts(mappedProducts);
        setError(null);
      } catch (err) {
        console.error("Error loading products:", err);
        setError(`Failed to load products. Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
  
    fetchProducts();
  }, [currentPage]);
  

  // Handle search with debounce — hits real API
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    if (!isAuthenticated) {
      setShowLoginNotice(true);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
        const res = await fetch(`${API_BASE}/products/search?q=${encodeURIComponent(searchQuery)}`, { headers });
        const wrapper = await res.json();
        const data = typeof wrapper.body === 'string' ? JSON.parse(wrapper.body) : (wrapper.body ?? wrapper);
        const products = data.products || (Array.isArray(data) ? data : []);
        setSearchResults(products.map(p => ({
          ...p,
          id: p.prod_id || p.id,
          image: p.image_url,
        })));
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, idToken]);

  // Handle sorting
  const handleSort = useCallback((key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
    
    const sortedProducts = [...filteredProducts].sort((a, b) => {
      if (key === 'novelty') {
        const dateA = new Date(a.novelty);
        const dateB = new Date(b.novelty);
        if (dateA < dateB) {
          return direction === 'asc' ? -1 : 1;
        }
        if (dateA > dateB) {
          return direction === 'asc' ? 1 : -1;
        }
        return 0;
      } else if (key === 'popularity') {
         if (a.popularity < b.popularity) {
          return direction === 'asc' ? 1 : -1; // Sort popularity descending by default
        }
        if (a.popularity > b.popularity) {
          return direction === 'asc' ? 1 : -1;
        }
        return 0;
      }
      
      if (a[key] < b[key]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setFilteredProducts(sortedProducts);
  }, [filteredProducts, sortConfig]);

  // Handle saving a product
  const handleSaveProduct = (productId) => {
    if (savedProducts.includes(productId)) {
      setSavedProducts(savedProducts.filter(id => id !== productId));
    } else {
      setSavedProducts([...savedProducts, productId]);
    }
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = currentPage === 1
  ? filteredProducts.slice(indexOfFirstItem, indexOfLastItem) // slice mock data
  : filteredProducts; // API page is already correct
//  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  // console.log("https://cosmetics-products-images-storage.s3.us-east-1.amazonaws.com/images/" + product.imageUrl )

  const handlePageChange = async (pageNumber) => {
    if (pageNumber < 1) return; // Prevent going below page 1
  
    // Prevent access if user is not logged in
    if (pageNumber > 1 && !isAuthenticated) {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        let idToken = null;
  
        if (typeof session.tokens?.idToken === "string") {
          idToken = session.tokens.idToken;
        } else if (session.tokens?.idToken?.jwtToken) {
          idToken = session.tokens.idToken.jwtToken;
        } else if (
          session.tokens?.idToken &&
          typeof session.tokens.idToken.toString === "function"
        ) {
          idToken = session.tokens.idToken.toString();
        }
  
        if (!idToken) throw new Error("User is not authenticated");
  
        setIsAuthenticated(true);
      } catch (error) {
        console.error(error);
        // Optionally redirect to login or show a message
        alert("You must be logged in to access this page.");

        return;
      }
    }
  
    setCurrentPage(pageNumber);
  };
  
  

  // Table columns configuration
  const columns = [
    {
      key: "image",
 header: "Image",
      width: "w-16", // Adjusted width
      render: (product) => (
        <div className="h-10 w-10 overflow-hidden rounded-md flex-shrink-0 bg-white flex items-center justify-center">
 {/* Ensure product.imageUrl is used if that's the correct API field */}
          <img
            src={"https://cosmelytix-products-imgs.s3.us-east-1.amazonaws.com/" + product.image} // Use imageUrl if available, fallback to image
            alt={product.name}
 className="h-full w-full object-contain"
//             onError={(e) => {
//               // Handle broken image links by replacing with a placeholder
//  e.target.src = "/assets/images/no_image.png"; // Path to your placeholder image
//             }}
          />

        </div>
      )
    },
    {
      key: "name",
      header: "Name",
      sortable: true,
      width: "w-1/6", // Adjusted width
      render: (product) => (
        <a
        href={`https://cosmelytix.com/products/${product.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-secondary hover:underline"
      >
        {product.name}
      </a>      )
    },
    {
      key: "brand",
      header: "Brand",
      sortable: true,
      width: "w-1/6", // Adjusted width
       render: (product) => (
        <span className="text-sm text-secondary">{product.brand || 'N/A'}</span>
      )
    },
    {
      key: "sustainability",
      header: "Sustainability",
      sortable: true,
      width: "w-1/20",
      render: (product) => (
        <span className="text-sm text-secondary max-w-[30px] ">{product.sustainability || ' '}</span>
      )
    },
    //  {
    //   key: "novelty",
    //   header: "Novelty",
    //   sortable: true,
    //   width: "w-1/8",
    //   render: (product) => {
    //     console.log('product', product)
    //     const publishDate = new Date(product.novelty);
    //     const timeDiff = Math.abs(new Date() - publishDate);
    //     const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
        
    //     if (daysDiff < 30) {
    //       return <span className="text-sm text-green-600 font-medium">New</span>;
    //     } else if (daysDiff < 180) {
    //        return <span className="text-sm text-yellow-600 font-medium">Recent</span>;
    //     }
    //      return <span className="text-sm text-secondary">Older</span>;
    //   }
    // },
    //  {
    //   key: "popularity",
    //   header: "Likes",
    //   sortable: true, // Can sort by popularity
    //   width: "w-20", 
    //   render: (product) => (
    //     <div className="flex items-center text-sm text-secondary">
    //       <Icon name="Heart" className="text-red-500 mr-1" size={14} />
    //       <span>{product.popularity || 0}</span>
    //     </div>
    //   )
    // },
     {
      key: "description",
      header: "Description",
      width: "flex-1", // Take remaining width
      render: (product) => (
        <div className="text-sm text-secondary line-clamp-4 max-w-[300px] break-words">{product.description || 'No description provided.'}</div>
      )
    },
    {
      key: "save",
      header: "Save",
      width: "w-16",
      render: (product) => {
        const isActive = interactions.save.has(product.id);
        return (
          <button
            onClick={() => handleInteraction(product.id, "save")}
            className="flex items-center justify-center"
          >
            <Bookmark className={`w-[18px] h-[18px] text-[#5F40A1] ${isActive ? "fill-[#5F40A1]" : ""}`} />
          </button>
        );
      },
    },
    {
      key: "like",
      header: "Like",
      width: "w-16",
      render: (product) => {
        const isActive = interactions.like.has(product.id);
        return (
          <button
            onClick={() => handleInteraction(product.id, "like")}
            className="flex items-center justify-center"
          >
            <Heart className={`w-[18px] h-[18px] text-pink-600 ${isActive ? "fill-pink-600" : ""}`} />
          </button>
        );
      },
    },
    {
      key: "add",
      header: "Add",
      width: "w-16",
      render: (product) => {
        const isActive = interactions.add.has(product.id);
        return (
          <button
            onClick={() => handleInteraction(product.id, "add")}
            className="flex items-center justify-center"
          >
            <PlusCircle className={`w-[18px] h-[18px] text-[#E9A969] ${isActive ? "fill-[#E9A969]" : ""}`} />
          </button>
        );
      },
    },
  ];

  // Render error state
  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <Icon name="AlertTriangle" size={48} className="text-error mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-secondary mb-2">Something went wrong</h2>
            <p className="text-secondary mb-6">{error}</p>
            <Button 
              onClick={() => window.location.reload()} 
              icon="RefreshCw"
            >
              Try Again
            </Button>
          </div>
        </main>
        <Footer variant="minimal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow py-8 px-4 sm:px-8 lg:px-8 bg-[#A89B8D]">
        {/* New section before product table */}
        
        {/* <div className="mb-8">
          <h1 className="text-3xl font-display text-secondary mb-2">PRODUCTS</h1>
          <p className="text-secondary">
            Browse our collection of premium cosmetics and skincare products
          </p>
        </div> */}

<div className="mb-10 w-full rounded-3xl bg-gradient-to-r from-[#E9A969] to-[#EDEAF4] shadow-xl overflow-hidden">
  <div className="flex flex-col sm:flex-row justify-between items-center gap-10 px-10 py-14">

    {/* Left Side - Hero Content */}
    <div className="flex-1 space-y-6 order-1 sm:order-1">
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#5F40A1] drop-shadow-sm">
        Smarter Skincare, <br />Backed by Science
      </h1>
      <p className="text-gray-700 text-lg sm:text-xl leading-relaxed max-w-xl">
        Explore thousands of products, ingredients, and brands with powerful insights.
        Cosmelytix helps you make confident, data-driven choices for healthier skin.
      </p>
      <button
        onClick={() => {
          document.getElementById("products-table")?.scrollIntoView({
            behavior: "smooth",
          });
        }}
        className="group relative inline-flex items-center justify-center overflow-hidden rounded-full bg-[#5F40A1] px-8 py-3 text-white font-medium transition-all hover:bg-[#4A2F82] shadow-md hover:shadow-lg"
      >
        <span className="absolute inset-0 h-full w-full bg-gradient-to-r from-[#7A5CCF] to-[#5F40A1] opacity-0 transition-opacity duration-300 group-hover:opacity-100"></span>
        <span className="relative">Explore Products</span>
      </button>
    </div>

    {/* Right Side - Stats */}
    <div className="flex flex-col sm:flex-row flex-1 justify-evenly sm:justify-center sm:gap-16 order-2 sm:order-2 w-full space-y-6 sm:space-y-0">
      {[
        { label: "Brands", value: "7,600+" },
        { label: "Products", value: "39,000+" },
        { label: "Ingredients", value: "12,000+" },
      ].map((stat, i) => (
        <div
          key={i}
          className="flex flex-col items-center bg-white/60 backdrop-blur-md px-6 py-6 rounded-2xl shadow-md hover:shadow-xl transition transform hover:-translate-y-1 w-full sm:w-auto"
        >
          <span className="text-4xl font-extrabold text-[#5F40A1]">
            {stat.value}
          </span>
          <span className="text-gray-600 text-sm mt-1">{stat.label}</span>
        </div>
      ))}
    </div>

  </div>
</div>



        
        <div id="products-table" className="bg-[#EDEAF4] border border-border rounded-lg shadow-sm overflow-hidden ">
          {/* Search bar */}
          <div className="p-4 border-b border-border">
            <input
              type="text"
              value={searchQuery}
              onChange={e => {
                setShowLoginNotice(false);
                setSearchQuery(e.target.value);
              }}
              placeholder="Search products by name, brand, ingredient..."
              className="w-full border border-[#5F40A1]/30 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F40A1]/40"
            />
            {showLoginNotice && (
              <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-[#5F40A1]/10 border border-[#5F40A1]/30 px-4 py-2 text-sm text-[#5F40A1]">
                <span>Please <a href="/user-account-page" className="font-semibold underline hover:text-[#4709CB]">sign in</a> to search products.</span>
                <button onClick={() => { setShowLoginNotice(false); setSearchQuery(''); }} className="text-[#5F40A1]/60 hover:text-[#5F40A1] font-bold text-base leading-none">✕</button>
              </div>
            )}
          </div>

          <ProductTable
            columns={columns}
            data={searchQuery.trim() ? searchResults : currentItems}
            loading={loading || isSearching}
            onSort={handleSort}
            sortConfig={sortConfig}
          />

          {!loading && !searchQuery.trim() && filteredProducts.length > 0 && (
            <div className="border-t border-border p-4 flex justify-center items-center gap-3">
    <Button
      variant="outline"
      size="sm"
      disabled={currentPage === 1}
      onClick={() => handlePageChange(currentPage - 1)}
      className="px-4 py-2 rounded-md hover:bg-gray-100 disabled:opacity-50"
    >
      Previous
    </Button>

    <span className="px-4 py-2 rounded-md bg-gray-100 text-sm font-medium">
      Page {currentPage}
    </span>

    <Button
      variant="outline"
      size="sm"
      onClick={() => handlePageChange(currentPage + 1)}
      className=" px-4 py-2 rounded-md hover:bg-gray-100"
    >
      Next
    </Button>
  </div>
          )}
          
          {!loading && !isSearching && searchQuery.trim() && searchResults.length === 0 && (
            <div className="py-16 text-center">
              <Icon name="Search" size={48} className="text-text-tertiary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary mb-1">No products found</h3>
              <p className="text-secondary">
                Try adjusting your search to find what you're looking for
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setSearchQuery("")}
              >
                Clear Search
              </Button>
            </div>
          )}
        </div>
        
        <div className="mt-8 bg-surface border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">
            Need more information?
          </h2>
          <p className="text-white mb-4">
            Check out our API documentation to learn how to integrate our products into your applications.
          </p>
          <a href="/api-documentation-page">
  <button className="w-full py-3 text-center text-white border-2 border-white rounded-full text-lg font-normal mt-6">
    View API Documentation
  </button>
</a>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ProductsHomePage;